import {
  ChangeEvent,
  HTMLProps,
  useEffect,
  useRef,
  KeyboardEvent,
  useCallback,
  useState,
} from "react";
import "./LinkDialog.css";
import { useLinkState } from "../../hooks/useLinkState";
import { CommandButton, CommandButtonGroup } from "@remirror/react-ui";
import { useAttrs } from "@remirror/react";

type Props = {
  nodeId: string;
};

const isValidUrl = (url: string) => {
  if (url === "") return true; // We want users to be able to remove the link completely

  try {
    new URL(url);
    return true;
  } catch (_) {
    return false;
  }
};

const DelayAutoFocusInput = (
  props: HTMLProps<HTMLInputElement> & { isValid: boolean; nodeId: string },
) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { onRemove } = useLinkState(props.nodeId);
  const url = (useAttrs().link()?.href as string) ?? "";

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
        inputRef.current.scrollLeft = 0; // Ensure the input scrolls to the beginning
      }
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, []);

  const openLink = useCallback(() => {
    window.open(url, "_blank", "noopener,noreferrer");
  }, [url]);

  return (
    <div className="link-input-wrapper">
      <input
        className={`link-input ${props.isValid ? "" : "error"}`}
        ref={inputRef}
        {...props}
      />
      {url && (
        <CommandButtonGroup>
          <CommandButton
            aria-label="Remove link"
            commandName="removeLink"
            onSelect={onRemove}
            icon="linkUnlink"
            enabled
          />
          <CommandButton
            aria-label="Open link"
            commandName="openLink"
            onSelect={openLink}
            icon="externalLinkFill"
            enabled
          />
        </CommandButtonGroup>
      )}
    </div>
  );
};

export function LinkDialog({ nodeId }: Props) {
  const { setHref, href, submitHref, cancelHref } = useLinkState(nodeId);
  const [isValid, setIsValid] = useState(true);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setHref(event.target.value);
      setIsValid(true);
    },
    [setHref],
  );

  return (
    <DelayAutoFocusInput
      nodeId={nodeId}
      placeholder="Type or paste link..."
      onChange={handleChange}
      value={href}
      isValid={isValid}
      onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
        const { code } = event;

        if (code === "Enter") {
          if (isValidUrl(href)) {
            submitHref();
          } else {
            setIsValid(false);
          }
        }

        if (code === "Escape") {
          cancelHref();
        }
      }}
    />
  );
}
